/**
 * Production-safe logging utility
 * Replaces console.* to prevent information leakage in production
 *
 * Behavior:
 * - DEV mode: All logs output to console
 * - PROD mode: Only errors output to console; warnings go to Sentry (deduplicated)
 * - PROD + VITE_DEBUG_LOGS=true: All logs output to console
 *
 * Use warnOnce(key, ...args) for warnings that should only be sent once per session.
 */

// Deduplication cache for warnings sent to Sentry (max 200 keys to prevent memory leak)
const WARN_CACHE_MAX_SIZE = 200;
const warnedKeys = new Set<string>();

class Logger {
  private isDev = import.meta.env.DEV;
  private isProd = import.meta.env.PROD;
  private debugEnabled = import.meta.env.VITE_DEBUG_LOGS === 'true';

  /**
   * Should output to console based on environment and debug flag
   */
  private shouldLog(level: 'log' | 'info' | 'debug' | 'warn' | 'error'): boolean {
    // In development, always log
    if (this.isDev) return true;

    // In production, only errors go to console by default
    if (level === 'error') return true;

    // In production, all other levels (including warn) require debug flag
    return this.debugEnabled;
  }

  /**
   * Check if a warning key has already been sent (for deduplication)
   */
  private hasWarned(key: string): boolean {
    return warnedKeys.has(key);
  }

  /**
   * Mark a warning key as sent (with size cap to prevent memory leak)
   */
  private markWarned(key: string): void {
    if (warnedKeys.size >= WARN_CACHE_MAX_SIZE) {
      // Clear oldest entries (simple strategy: clear all when full)
      warnedKeys.clear();
    }
    warnedKeys.add(key);
  }

  /**
   * General log - replaces console.log
   */
  log(...args: unknown[]) {
    if (this.shouldLog('log')) {
      // eslint-disable-next-line no-console
      console.log(...args);
    }
  }

  /**
   * Info logs - replaces console.info
   */
  info(...args: unknown[]) {
    if (this.shouldLog('info')) {
      // eslint-disable-next-line no-console
      console.info(...args);
    }
  }

  /**
   * Debug logs - replaces console.debug
   */
  debug(...args: unknown[]) {
    if (this.shouldLog('debug')) {
      // eslint-disable-next-line no-console
      console.debug(...args);
    }
  }

  /**
   * Warning logs - console output gated by environment
   * In PROD: suppressed from console, sent to Sentry (deduplicated by message)
   * In DEV or with VITE_DEBUG_LOGS=true: output to console
   */
  warn(...args: unknown[]) {
    // Console output only in DEV or with debug flag
    if (this.shouldLog('warn')) {
      // eslint-disable-next-line no-console
      console.warn(...args);
    }

    // In production, send to Sentry (deduplicated by first arg as key)
    if (this.isProd && typeof window !== 'undefined' && window.Sentry) {
      const message = String(args[0]);
      // Deduplicate: only send each unique warning once per session
      if (!this.hasWarned(message)) {
        this.markWarned(message);
        window.Sentry.captureMessage(message, 'warning');
      }
    }

    // Also send to gtag if available (kept for backwards compatibility)
    if (this.isProd && typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'app_warning', {
        message: String(args[0]),
      });
    }
  }

  /**
   * Warning that should only be logged once per session (by explicit key)
   * Useful for warnings that may fire repeatedly but only need to be seen once
   */
  warnOnce(key: string, ...args: unknown[]) {
    // In DEV, always show (but still dedupe console)
    if (this.isDev) {
      if (!this.hasWarned(key)) {
        this.markWarned(key);
        // eslint-disable-next-line no-console
        console.warn(...args);
      }
      return;
    }

    // In PROD with debug flag, show in console (deduplicated)
    if (this.debugEnabled && !this.hasWarned(key)) {
      this.markWarned(key);
      // eslint-disable-next-line no-console
      console.warn(...args);
    }

    // In PROD, send to Sentry (deduplicated by key)
    if (this.isProd && typeof window !== 'undefined' && window.Sentry) {
      if (!this.hasWarned(key)) {
        this.markWarned(key);
        const message = String(args[0]);
        window.Sentry.captureMessage(message, 'warning');
      }
    }
  }

  /**
   * Error logs - always visible in console, sent to Sentry
   */
  error(...args: unknown[]) {
    if (this.shouldLog('error')) {
      // eslint-disable-next-line no-console
      console.error(...args);
    }

    // Send to Sentry in production
    if (this.isProd && typeof window !== 'undefined' && window.Sentry) {
      const error = args.find((arg) => arg instanceof Error);
      const message = String(args[0]);

      if (error) {
        window.Sentry.captureException(error);
      } else {
        window.Sentry.captureMessage(message, 'error' as any);
      }
    }
  }

  /**
   * Performance timing - only in development or with debug flag
   */
  time(label: string) {
    if (this.shouldLog('debug')) {
      // eslint-disable-next-line no-console
      console.time(label);
    }
  }

  timeEnd(label: string) {
    if (this.shouldLog('debug')) {
      // eslint-disable-next-line no-console
      console.timeEnd(label);
    }
  }
}

export const logger = new Logger();

// Type augmentation for gtag and Sentry
declare global {
  interface Window {
    gtag?: (command: string, eventName: string, params?: Record<string, unknown>) => void;
    Sentry?: {
      captureException: (error: Error) => void;
      captureMessage: (message: string, level: any) => void;
    };
  }
}
